/**
 * IPFS Upload Service
 * Handles file uploads to Filebase via Vercel API
 */

export interface UploadResponse {
  success: boolean;
  filename: string;
  size: number;
  contentType: string;
  cid: string;
  ipfsUri: string;
  ipfsGateways: {
    filebase: string;
    ipfsio: string;
    dweb: string;
    pinata: string;
  };
  s3Url: string;
  uploadedAt: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

class IPFSUploadService {
  private apiEndpoint = '/api/upload';

  /**
   * Upload a file to IPFS via Filebase
   */
  async uploadFile(
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress: UploadProgress = {
              loaded: event.loaded,
              total: event.total,
              percentage: Math.round((event.loaded / event.total) * 100),
            };
            onProgress(progress);
          }
        });
      }

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed due to network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was cancelled'));
      });

      // Send request
      xhr.open('POST', this.apiEndpoint);
      xhr.send(formData);
    });
  }

  /**
   * Upload JSON metadata to IPFS
   */
  async uploadMetadata(metadata: any): Promise<UploadResponse> {
    const jsonBlob = new Blob([JSON.stringify(metadata, null, 2)], {
      type: 'application/json',
    });
    const file = new File([jsonBlob], 'metadata.json', {
      type: 'application/json',
    });

    return this.uploadFile(file);
  }

  /**
   * Fetch metadata from IPFS with gateway fallback
   */
  async fetchMetadata(uri: string): Promise<any> {
    const gateways = [
      'https://ipfs.filebase.io/ipfs/',
      'https://ipfs.io/ipfs/',
      'https://dweb.link/ipfs/',
      'https://gateway.pinata.cloud/ipfs/',
    ];

    // Extract CID from URI
    let cid = uri;
    if (uri.startsWith('ipfs://')) {
      cid = uri.replace('ipfs://', '');
    } else if (uri.startsWith('http')) {
      // Extract CID from gateway URL
      const match = uri.match(/\/ipfs\/([^/]+)/);
      if (match) {
        cid = match[1];
      }
    }

    // Try each gateway
    for (const gateway of gateways) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(`${gateway}${cid}`, {
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          return await response.json();
        }
      } catch (error) {
        console.warn(`Gateway ${gateway} failed, trying next...`);
      }
    }

    throw new Error('All IPFS gateways failed');
  }

  /**
   * Convert IPFS URI to HTTP gateway URL
   */
  getGatewayUrl(uri: string, gateway: 'filebase' | 'ipfsio' | 'dweb' | 'pinata' = 'filebase'): string {
    const gateways = {
      filebase: 'https://ipfs.filebase.io/ipfs/',
      ipfsio: 'https://ipfs.io/ipfs/',
      dweb: 'https://dweb.link/ipfs/',
      pinata: 'https://gateway.pinata.cloud/ipfs/',
    };

    let cid = uri;
    if (uri.startsWith('ipfs://')) {
      cid = uri.replace('ipfs://', '');
    }

    return `${gateways[gateway]}${cid}`;
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File, maxSizeMB: number = 100): { valid: boolean; error?: string } {
    // Check file size
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      return {
        valid: false,
        error: `File size exceeds ${maxSizeMB}MB limit`,
      };
    }

    // Check file type (allow images, videos, documents, JSON)
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/svg+xml',
      'image/webp',
      'video/mp4',
      'video/webm',
      'application/pdf',
      'application/json',
      'text/plain',
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'File type not supported',
      };
    }

    return { valid: true };
  }
}

export default new IPFSUploadService();
